import React, { useContext, useEffect, useMemo, useRef, useState } from "react";

import EventLogPlay from "./EventLogPlay/EventLogPlay";
import EventLogFilter from "./EventLogFilter/EventLogFilter";
import defaultSetup from "../../../Config/defaultSetupConfiguration";
import { FixtureDetailsContext } from "../../../Config/FixtureDetails";
import EventLogDropArea from "./EventLogDropArea/EventLogDropArea";
import { sendEvent } from "../../Base/Utilities/SendEvent";

import "./EventLog.scss";

const hiddenEvents = [
  {
    eventType: "videoReview",
    subType: "pending",
  },
  {
    eventType: "suspension",
    options: ["possible"],
  },
  {
    eventType: "yellowCard",
    options: ["possible"],
  },
  {
    eventType: "redCard",
    options: ["possible"],
  },
  {
    eventType: "sevenMetrePenalty",
    options: ["possible"],
  },
];

const defaultFilterValues = {
  period: -1,
  eventType: -1,
  personId: -1,
  flagged: -1,
};

const EventLog = (props) => {
  const { updateLog, updateState, currentState, eventStore, playStore } = props;
  const { eventToReview, hasUnsentEvents } = currentState;
  const { sport } = useContext(FixtureDetailsContext);
  const [periods, setPeriods] = useState();
  const [filterValues, setFilterValues] = useState({ ...defaultFilterValues });
  const [filterActive, setFilterActive] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [toDropOnPlay, setToDropOnPlay] = useState(null);
  const [toDropPlay, setToDropPlay] = useState(null);
  const [rootScrollTop, setRootScrollTop] = useState(0);
  const [dropContextPlays, setDropContextPlays] = useState([]);
  const eventLogRootRef = useRef();

  const getDefaultPeriodFilter = () => {
    if (sport === "handball") {
      return "All";
    } else {
      return currentState.period.periodId;
    }
  };

  const areFiltersChanged = (filters) => {
    const filterKeys = Object.keys(defaultFilterValues).filter((k) => k !== "period"); // filter out period, because we check it differently
    const nonPeriodFiltersChanged = filterKeys.some((key) => filters[key] !== defaultFilterValues[key]);
    const periodFilterChanged = filters.period !== getDefaultPeriodFilter();
    return nonPeriodFiltersChanged || periodFilterChanged;
  };

  useEffect(() => {
    const periodFilter = getDefaultPeriodFilter();
    setFilterValues({ ...filterValues, period: periodFilter });
  }, [sport]);

  useEffect(() => {
    if (filterValues.period !== "All" && filterValues.period !== -1) {
      setFilterValues({ ...filterValues, period: currentState.period.periodId });
    }
  }, [currentState.period.periodId]);

  useEffect(() => {
    updateState("updateLog", Math.random());
    // eslint-disable-next-line
  }, [JSON.stringify(currentState.acks)]);

  useEffect(() => {
    if (eventToReview) {
      setFilterValues({
        ...filterValues,
        period: parseInt(eventToReview.metadata.periodId, 10),
      });
    }

    // eslint-disable-next-line
  }, [eventToReview]);

  useEffect(() => {
    if (!isDragging && !toDropPlay) {
      setToDropOnPlay(null);
    }
  }, [isDragging, toDropPlay]);

  useEffect(() => {
    if (toDropPlay) {
      if (toDropOnPlay) {
        const sortedPlays = dropContextPlays.sort((a, b) =>
          new Date(a.eventTime.replace("Z", "")) > new Date(b.eventTime.replace("Z", "")) ? -1 : 1,
        );

        const afterIdx = toDropOnPlay.index;
        if (sortedPlays[afterIdx - 1]?.playId !== toDropPlay.playId) {
          const beforeIdx = afterIdx - 1;
          setPlayEventTimeToMidpoint(toDropPlay, sortedPlays[beforeIdx], sortedPlays[afterIdx]);
        }
      }
      setToDropPlay(null);
    }
  }, [toDropPlay, toDropOnPlay, dropContextPlays]);

  useEffect(() => {
    const elem = eventLogRootRef.current;
    if (!elem) {
      return;
    }
    const handleScroll = (e) => {
      setRootScrollTop(elem.scrollTop);
    };

    elem.addEventListener("scroll", handleScroll);
    return () => {
      elem.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    // Set the filter to show only unsent events
    if (hasUnsentEvents) {
      setFilterValues({ ...filterValues, eventType: "unsent" });
      setFilterActive(true);
      updateState("hasUnsentEvents", null);
    }
  }, [hasUnsentEvents]);

  function setFilter(key, e) {
    const newFilter = filterValues;
    newFilter[key] = e.target.value === "-1" ? -1 : e.target.value;
    setFilterValues(newFilter);
    setFilterActive(areFiltersChanged(newFilter));
    props.forceUpdate();
  }

  function clearFilters() {
    const newFilters = { ...defaultFilterValues };
    newFilters.period = getDefaultPeriodFilter();
    setFilterValues(newFilters);
    setFilterActive(false);
    props.forceUpdate();
  }

  function personList() {
    const personsList = [];
    const entities = props.currentState?.entities || [];
    entities.forEach((entity) => {
      entity.persons
        .sort((a, b) => (parseInt(a.bib) < parseInt(b.bib) ? -1 : 1))
        .forEach((person) => {
          personsList.push(person);
        });
    });
    return personsList;
  }

  function isEventHidden(event) {
    return hiddenEvents.some((hiddenEvent) =>
      Object.keys(hiddenEvent).every((key) => {
        if (key === "options") {
          return hiddenEvent["options"].every((opt) => Object.keys(event["options"] ?? {}).includes(opt));
        }
        return hiddenEvent[key] === event[key];
      }),
    );
  }

  function setPlayEventTimeToMidpoint(play, beforePlay, afterPlay) {
    if (!play || !beforePlay || !afterPlay) {
      return;
    }

    const playDate = new Date(`${play.eventTime}${play.eventTime.includes("Z") ? "" : "Z"}`);
    const beforePlayDate = new Date(`${beforePlay.eventTime}${beforePlay.eventTime.includes("Z") ? "" : "Z"}`);
    const afterPlayDate = new Date(`${afterPlay.eventTime}${afterPlay.eventTime.includes("Z") ? "" : "Z"}`);
    const newPlayDate = new Date(Math.floor((beforePlayDate.getTime() + afterPlayDate.getTime()) / 2));
    let newClock = null;
    if (playDate > beforePlayDate) {
      newClock = beforePlay.clock;
    } else {
      newClock = afterPlay.clock;
    }
    const playEvents = eventStore
      .filter((event) => event.playId === play.playId)
      .filter((event) => event.class === "sport")
      .filter((event) => !isEventHidden(event))
      .filter((event) => event.status !== "deleted");
    if (playEvents.length > 0) {
      playEvents.forEach((event) => {
        const sendAction = {
          action: "sendEvent",
          event: {
            ...event,
            status: "updated",
            clock: newClock,
            eventTime: newPlayDate.toISOString(),
          },
          type: "custom",
        };
        sendEvent(sendAction, props);
      });
    }
  }

  const playDisplay = useMemo(() => {
    if (playStore !== null && eventStore !== null) {
      let plays = eventStore;
      plays = plays.filter((event) => event.class === "sport");
      if (filterValues.period !== "All") {
        plays = plays.filter((event) => event.periodId === parseInt(filterValues.period));
      }
      switch (filterValues.eventType) {
        case "scores":
          plays = plays.filter(
            (event) =>
              event.eventType !== "sevenMetrePenalty" && Object.keys(defaultSetup.scoreTypes).includes(event.eventType),
          );
          break;
        case "fouls":
          plays = plays.filter((event) => defaultSetup.foulTypes.includes(event.eventType));
          break;
        case "foulBy":
          plays = plays.filter(
            (event) =>
              event.eventType === "foul" &&
              ["personal", "offensive", "unsportsmanlike", "technical", "throwIn", "disqualifying"].includes(
                event.subType,
              ),
          );
          break;
        case "foulDrawn":
          plays = plays.filter((event) => event.eventType === "foul" && event.subType === "drawn");
          break;
        case "fastBreak":
          plays = plays.filter(
            (event) => ["2pt", "3pt", "freeThrow"].includes(event.eventType) && event?.options?.fastBreak === true,
          );
          break;
        case "assist":
        case "substitution":
        case "block":
        case "rebound":
        case "steal":
        case "turnover":
        case "sevenMetrePenalty":
        case "freeThrow":
        case "timeOut":
          plays = plays.filter((event) => event.eventType === filterValues.eventType);
          break;
        case "sanctions":
          plays = plays.filter((event) => defaultSetup.sanctionTypes.includes(event.eventType));
          break;
        case "unsent":
          plays = plays.filter((event) => event.pending);
          break;
        default:
          break;
      }

      if (filterValues.personId !== -1) {
        plays = plays.filter(
          (event) => event.personId === filterValues.personId || event.options?.goalKeeperId === filterValues.personId,
        );
      }

      if (filterValues.flagged !== -1) {
        plays = plays.filter((event) => event.flagged);
      }

      let uniquePlays = [...new Set(plays.map((play) => play.playId))];
      let tempPeriods = [...new Set(playStore.filter((play) => !!play.periodId).map((play) => play.periodId))];

      plays = playStore.filter((play) => uniquePlays.includes(play.playId)).filter((play) => !!play.playId);

      if (currentState.period && !tempPeriods.includes(currentState.period.periodId)) {
        tempPeriods.unshift(currentState.period.periodId);
      }
      setPeriods(tempPeriods);

      const handlePlayDragged = (dragged) => {
        setIsDragging(dragged);
      };

      const handlePlayHovered = (play, index, hovered) => {
        if (isDragging && hovered) {
          setToDropOnPlay({ index: index, play: play });
        }
      };

      const handlePlayDropped = (play) => {
        setDropContextPlays(plays);
        setToDropPlay(play);
      };

      return (
        <div className="event-log-play-list">
          {plays
            .sort((a, b) => (new Date(a.eventTime.replace("Z", "")) > new Date(b.eventTime.replace("Z", "")) ? -1 : 1))
            .map((play, index) => {
              let playEvents = eventStore
                .filter((event) => event.playId === play.playId)
                .filter((event) => event.class === "sport")
                .filter((event) => !isEventHidden(event));
              if (playEvents.filter((ev) => ev.status !== "deleted").length > 0) {
                return (
                  <React.Fragment key={play.playId}>
                    {toDropOnPlay?.play?.playId === play.playId && index !== 0 && <EventLogDropArea {...props} />}
                    <EventLogPlay
                      playIndex={index}
                      key={play.playId}
                      play={play}
                      events={playEvents}
                      isBelowDropOn={toDropOnPlay?.index && index >= toDropOnPlay.index}
                      eventLogScroll={rootScrollTop}
                      onPlayDragged={handlePlayDragged}
                      onPlayHovered={
                        index !== 0
                          ? (play, hovered) => {
                              handlePlayHovered(play, index, hovered);
                            }
                          : null
                      }
                      onPlayDropped={handlePlayDropped}
                      {...props}
                    />
                  </React.Fragment>
                );
              } else {
                return null;
              }
            })}
        </div>
      );
    }
    // eslint-disable-next-line
  }, [updateLog, JSON.stringify(filterValues), eventStore, eventToReview, isDragging, toDropOnPlay, rootScrollTop]);

  return (
    <React.Fragment>
      <div
        className={"event-log enabled-" + (currentState.modify && currentState.modify.playId ? "false" : "true")}
        ref={eventLogRootRef}
      >
        <EventLogFilter
          periods={periods}
          setFilter={setFilter}
          clearFilters={clearFilters}
          filterValues={filterValues}
          filterActive={filterActive}
          personList={personList()}
        />
        {playDisplay}
      </div>
    </React.Fragment>
  );
};

export default React.memo(EventLog);
